import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Save, Sparkles, Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROVENANCE_OPTIONS = [
  { key: "regeneratively-farmed", label: "Regeneratively Farmed" },
  { key: "indigenous-grown", label: "Indigenous-Grown" },
  { key: "wildharvested", label: "Wildharvested on Country" },
  { key: "australian-made", label: "Australian-Made" },
  { key: "palm-free", label: "Palm-Free" },
  { key: "vegan", label: "Vegan" },
  { key: "small-batch", label: "Small Batch" },
  { key: "community-supporting", label: "Supports Indigenous Community" },
];

const IMG_STYLES = [
  { id: "botanical", label: "Botanical" },
  { id: "photographic", label: "Photographic" },
  { id: "painterly", label: "Painterly" },
];

const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const Field = ({ label, value, onChange, multiline, rows = 3, testid, placeholder }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">{label}</label>
    {multiline ? (
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-3 outline-none text-[#2D322B] text-sm" />
    ) : (
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B] text-sm" />
    )}
  </div>
);

function ProductEditor({ product, onSaved }) {
  const [p, setP] = useState(product);
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(null);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const toggleProvenance = (key) => {
    setP((x) => {
      const cur = Array.isArray(x.provenance) ? x.provenance : [];
      return { ...x, provenance: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
    });
  };

  const genImage = async (style) => {
    setImgBusy(`gen:${style}`);
    try {
      const r = await api.post("/admin/library/generate-image", { title: p.name, category: p.category || "Apothecary", style });
      set("image_url", r.data.url);
      toast.success("Image generated — Save to keep it.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate image");
    } finally {
      setImgBusy(null);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/apothecary/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", r.data.url);
      toast.success("Photo uploaded — Save to keep it.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload image");
    } finally {
      setImgBusy(null);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: p.name, subtitle: p.subtitle, tagline: p.tagline, why_chosen: p.why_chosen,
        price_aud: p.price_aud === "" || p.price_aud == null ? null : Number(p.price_aud),
        image_url: p.image_url || null, external_url: p.external_url, category: p.category,
        tags: p.tags, provenance: p.provenance, sort_order: p.sort_order,
      };
      const r = await api.put(`/admin/apothecary/${p.id}`, payload);
      onSaved(r.data);
      toast.success(`${p.name} updated — live now.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6" data-testid={`apothecary-editor-${p.id}`}>
      <div className="grid md:grid-cols-[180px_1fr] gap-6">
        {/* Image column */}
        <div className="space-y-3">
          <div className="aspect-square bg-[#F9F6F0] border border-[#D1C7B8] rounded-xl overflow-hidden flex items-center justify-center">
            {p.image_url ? (
              <img src={imgSrc(p.image_url)} alt={p.name} data-testid={`apothecary-preview-${p.id}`} className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-[#9A8F7E] px-3 text-center">No image — placeholder shown live</span>
            )}
          </div>
          <label className="btn-secondary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5 cursor-pointer w-full justify-center" data-testid={`apothecary-upload-${p.id}`}>
            <Upload className="w-3 h-3" /> {imgBusy === "upload" ? "Uploading…" : "Upload photo"}
            <input type="file" accept="image/*" onChange={uploadImage} className="hidden" />
          </label>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] uppercase tracking-widest text-[#5C6359] w-full">Or generate:</span>
            {IMG_STYLES.map((st) => (
              <button key={st.id} type="button" onClick={() => genImage(st.id)} disabled={!!imgBusy}
                data-testid={`apothecary-gen-${p.id}-${st.id}`}
                className="btn-secondary !py-1 !px-2 !text-[11px] inline-flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5" /> {imgBusy === `gen:${st.id}` ? "…" : st.label}
              </button>
            ))}
          </div>
        </div>

        {/* Fields column */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="label-eyebrow">{p.id}</p>
            <a href={p.external_url} target="_blank" rel="noreferrer" className="text-[11px] uppercase tracking-widest text-[#C05C42] hover:text-[#2D322B] inline-flex items-center gap-1">
              Store page <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <Field label="Name" value={p.name} onChange={(v) => set("name", v)} testid={`apothecary-name-${p.id}`} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price (AUD)" value={p.price_aud} onChange={(v) => set("price_aud", v)} testid={`apothecary-price-${p.id}`} placeholder="0.00" />
            <Field label="Category" value={p.category} onChange={(v) => set("category", v)} testid={`apothecary-category-${p.id}`} />
          </div>
          <Field label="Subtitle" value={p.subtitle} onChange={(v) => set("subtitle", v)} testid={`apothecary-subtitle-${p.id}`} />
          <Field label="Tagline" value={p.tagline} onChange={(v) => set("tagline", v)} testid={`apothecary-tagline-${p.id}`} />
          <Field label="Why chosen" value={p.why_chosen} onChange={(v) => set("why_chosen", v)} testid={`apothecary-why-${p.id}`} multiline rows={4} />
          <Field label="Image URL (or use upload/generate)" value={p.image_url} onChange={(v) => set("image_url", v)} testid={`apothecary-imageurl-${p.id}`} />

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-2">Provenance / sourcing badges</label>
            <div className="flex flex-wrap gap-2" data-testid={`apothecary-provenance-${p.id}`}>
              {PROVENANCE_OPTIONS.map((opt) => {
                const active = Array.isArray(p.provenance) && p.provenance.includes(opt.key);
                return (
                  <button key={opt.key} type="button" onClick={() => toggleProvenance(opt.key)}
                    data-testid={`apothecary-prov-${p.id}-${opt.key}`}
                    className={`text-[10px] uppercase tracking-[1.5px] rounded-full px-2.5 py-1 border transition-colors ${
                      active ? "bg-[#3F6B4C] text-white border-[#3F6B4C]" : "bg-transparent text-[#5C6359] border-[#CBD9BE] hover:border-[#3F6B4C]"
                    }`}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pt-2">
            <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-5 inline-flex items-center gap-2" data-testid={`apothecary-save-${p.id}`}>
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminApothecary() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/apothecary").then((r) => setProducts(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const onSaved = (updated) => {
    if (!updated) return;
    setProducts((arr) => arr.map((p) => (p.id === updated.id ? updated : p)));
  };

  const seedNativeFoods = async () => {
    if (!window.confirm("Generate the native-food starter writings? Massey will draft 4 articles with images in the background (~2 min). They'll appear in the Apothecary's Plant Medicine Library and in Curation → Library.")) return;
    setSeeding(true);
    try {
      const r = await api.post("/admin/apothecary/writings/seed-native-foods");
      toast.success(`Generating ${r.data?.count || 4} native-food writings in the background — they'll appear shortly.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start generation");
    } finally { setSeeding(false); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-apothecary-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="label-eyebrow mb-2">Admin · Apothecary</p>
            <h1 className="font-serif text-4xl text-[#2D322B] leading-tight">Tend the shelf.</h1>
          </div>
          <Link to="/apothecary" target="_blank" className="text-xs uppercase tracking-widest text-[#C05C42] hover:text-[#2D322B] inline-flex items-center gap-1.5" data-testid="apothecary-view-live">
            View live <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
        <p className="mt-4 text-[#5C6359] max-w-2xl text-sm">
          Edit product photos, prices, taglines and sourcing badges. Upload a real product photo or generate one,
          then Save — edits are live the moment you save. Store links are managed in code.
        </p>

        <section className="mt-6 bg-[#2D322B] text-[#F9F6F0] rounded-2xl p-6" data-testid="native-food-seed-box">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="label-eyebrow !text-[#D49A44] mb-1">Plant Medicine Library</p>
              <h2 className="font-serif text-2xl">Generate native-food writings</h2>
              <p className="text-sm text-[#F9F6F0]/75 mt-2 max-w-xl">
                Massey drafts starter articles on native foods as medicine (Kakadu plum, wattleseed, gumbi gumbi,
                native superfoods) with images. They appear in the Apothecary's Plant Medicine Library and link to
                matching products automatically. Edit any of them in the <strong>Curation → Library</strong> tab.
              </p>
            </div>
            <button onClick={seedNativeFoods} disabled={seeding} data-testid="seed-native-foods"
              className="btn-primary !bg-[#D49A44] hover:!bg-[#bf8a3a] !text-[#2D322B] inline-flex items-center gap-2 shrink-0">
              <Sparkles className="w-4 h-4" /> {seeding ? "Starting…" : "Generate native-food writings"}
            </button>
          </div>
        </section>

        {loading ? (
          <p className="mt-8 text-[#5C6359]" data-testid="admin-apothecary-loading">Loading shelf…</p>
        ) : (
          <div className="mt-8 space-y-5">
            {products.map((p) => (
              <ProductEditor key={p.id} product={p} onSaved={onSaved} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
